import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DashboardIcon2 from "static/svgs/dashboard2";
import LogoIcon2 from "static/svgs/logo2";
import BugReportIcon from "static/svgs/BugReport";
import DevicesIcon from "static/svgs/Devices";
import NetworkIcon from "static/svgs/Network";
import SettingsIcon from "static/svgs/Settings";
import LogoutIcon from "static/svgs/Logout";
import styles from "./quantSidebar.module.scss";
import layoutStyles from "./layout.module.scss";
import { Box, Drawer } from "@mui/material";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import TaskOutlinedIcon from "@mui/icons-material/TaskOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import SecureBootIcon from "static/svgs/SecureBoot";
import ProfileIcon from "static/svgs/Profile";
import { USER_LOGOUT } from "../../../redux/types/auth.types";

const navSections = [
  {
    title: "Overview",
    items: [
      {
        key: "ciso-dashboard",
        label: "CISO Dashboard",
        icon: DashboardOutlinedIcon,
        link: "/",
      },
      {
        key: "soc-dashboard",
        label: "SOC Dashboard",
        icon: SecurityOutlinedIcon,
        link: "/soc-dashboard",
      },
    ],
  },
  {
    title: "Security",
    items: [
      {
        key: "zero-trust-policies",
        label: "Zero Trust Policies",
        icon: VerifiedUserOutlinedIcon,
        link: "/zero-trust-policies",
      },
      {
        key: "threats-incidents",
        label: "Threats & Incidents",
        icon: WarningAmberOutlinedIcon,
        link: "/threats-incidents",
      },
    ],
  },
  {
    title: "Infrastructure",
    items: [
      { label: "Secure Boot", link: "/secureboot", icon: SecureBootIcon },

      {
        key: "assets-devices",
        label: "Assets & Devices",
        icon: DevicesIcon,
        link: "/single-dashboard",
      },
      {
        key: "network-topology",
        label: "Network & Topology",
        icon: NetworkIcon,
        link: "/networks",
      },
    ],
  },
  {
    title: "Governance",
    items: [
      {
        key: "compliance-reports",
        label: "Compliance & Reports",
        icon: TaskOutlinedIcon,
        link: "/compliance-reports",
      },
    ],
  },
  {
    title: "System",
    items: [
      { label: "Bug Report", link: "/bug-report", icon: BugReportIcon },

      {
        label: "Portal Management",
        link: "/portal-access-management",
        icon: ProfileIcon,
      },
      {
        key: "settings",
        label: "Settings",
        icon: SettingsIcon,
        link: "/account",
      },
    ],
  },
];

/**
 * A static vertical sidebar that matches the provided design.
 * Use `activeKey` to control the highlighted item; falls back to pathname.
 */
const QuantSidebar = ({ activeKey, onSelect, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(true);
  const normalizedPath = location.pathname
    .toLowerCase()
    .includes("cdashboard")
    ? "/"
    : location.pathname;
  const currentKey = activeKey || normalizedPath;
  const isActivePath = (path, link) =>
    path === link || (!!link && link !== "/" && path.startsWith(link));

  const handleClick = (item) => {
    if (onSelect) {
      onSelect(item);
    }
    if (item.link) {
      navigate(item.link);
    }
  };

  const handleLogout = () => {
    dispatch({ type: USER_LOGOUT });
    navigate("/login");
  };

  return (
    <Box display={"flex"}>
      <Drawer variant="permanent" open={open} sx={{ zIndex: 20 }}>
        <div className={`${styles.sidebar} ${!open ? styles.collapsed : ""}`}>
          <div className={styles.brand} onClick={() => navigate("/")}>
            {open ? (
              <LogoIcon2 fill={"#fff"} />
            ) : (
              <DashboardIcon2 width="30" height="30" />
            )}
          </div>
          <button
            className={styles.toggle}
            onClick={() => setOpen((prev) => !prev)}
            aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
          >
            {"<"}
          </button>

          <div className={styles.content}>
            {navSections.map((section) => (
              <div key={section.title}>
                <div className={styles.sectionTitle}>{section.title}</div>
                <div className={styles.navList}>
                  {section.items.map((item) => {
                    const isActive =
                      isActivePath(currentKey, item.link) ||
                      currentKey === item.key;
                    const Icon = item.icon;
                    return (
                      <div
                        key={item.key}
                        className={`${styles.navItem} ${
                          isActive ? styles.active : ""
                        }`}
                        onClick={() => handleClick(item)}
                      >
                        {Icon ? (
                          <span className={styles.icon}>
                            <Icon fill="#ffffff" />
                          </span>
                        ) : null}
                        <span className={styles.navItemText}>{item.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className={styles.footer}>
            <div className={styles.navItem} onClick={handleLogout}>
              <span className={styles.icon}>
                <LogoutIcon fill="#ffffff" />
              </span>
              <span className={styles.navItemText}>Logout</span>
            </div>
          </div>
        </div>
      </Drawer>
      <Box
        component="main"
        className={
          open
            ? layoutStyles.newMainContainerOpen
            : layoutStyles.newMainContainerClose
        }
      >
        {children}
      </Box>
    </Box>
  );
};

export default QuantSidebar;
